import { useState, useEffect, useMemo, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { instrumentsAPI } from "@/services/api";
import { 
  Search, Plus, Trash2, Copy, RefreshCw, Download, Upload,
  ChevronDown, ChevronUp, Edit, X, Info, DollarSign, Activity
} from "lucide-react";
import { toast } from "sonner";
import LeverageSelector, { LeverageBadge } from "@/components/LeverageSelector";
import VirtualPriceController from "@/components/VirtualPriceController";
import InstrumentManagement from "@/components/InstrumentManagement";
import { settingsToLegacy, settingsFromLegacy } from '../admin/parity/policyMapping';
import { useLegacyControlIds } from '../admin/parity/useLegacyControlIds';
import { EnginePolicyFields } from '../admin/parity/EnginePolicyFields';
import { InstrumentPolicySection as PolicySection } from '../components/InstrumentPolicySection';
import '../components/instrument-policy.css';

const TradingTerms = () => {
  return (
    <Routes>
      <Route index element={<TradingTermsList />} />
      <Route path="instruments" element={<InstrumentManagement />} />
    </Routes>
  );
};

const TradingTermsList = () => {
  const [terms, setTerms] = useState([
    { id: 1, name: "Default TRY", lastUpdate: "27/03/2026 14:00", accounts: 1 },
  ]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [search, setSearch] = useState("");

  const filteredTerms = terms.filter(t => 
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-4">
          <h1 className="sirix-page-title" style={{ padding: 0, margin: 0 }}>TRADING TERMS</h1>
          <div className="sirix-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="sirix-btn sirix-btn-primary">
            <Plus size={14} />
          </button>
          <button className="sirix-btn sirix-btn-secondary">
            <Trash2 size={14} />
            Delete
          </button>
          <button className="sirix-btn sirix-btn-secondary">
            <Copy size={14} />
            Duplicate
          </button>
          <button className="sirix-btn sirix-btn-secondary">
            <RefreshCw size={14} />
            Margin Recalculation
          </button>
          <button className="sirix-btn sirix-btn-secondary">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto bg-white">
        <table className="sirix-table">
          <thead>
            <tr>
              <th>Name ▲</th>
              <th>Last Update</th>
              <th>Accounts</th>
            </tr>
          </thead>
          <tbody>
            {filteredTerms.map((term) => (
              <tr 
                key={term.id}
                className={selectedTerm?.id === term.id ? "selected" : ""}
                onClick={() => setSelectedTerm(term)}
                onDoubleClick={() => {
                  setSelectedTerm(term);
                  setShowEditModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                <td className="font-semibold">{term.name}</td>
                <td className="font-mono text-xs">{term.lastUpdate}</td>
                <td className={selectedTerm?.id === term.id ? "bg-[#00BCD4] text-white" : ""}>
                  {term.accounts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="sirix-table-footer">
        <div>Count={filteredTerms.length}</div>
        <div></div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedTerm && (
        <TradingTermsModal 
          term={selectedTerm} 
          onClose={() => setShowEditModal(false)} 
        />
      )}
    </div>
  );
};

// Edit Instruments Policy Modal - Expandable sections like SiRiX
const EditInstrumentsPolicyModal = ({ instruments, onClose, onSave, scoped = false }) => {
  const controlRoot=useRef(null);useLegacyControlIds(controlRoot,'instrument-policy');
  const isBulk = instruments.length > 1;
  const firstInst = instruments[0];
  
  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    contractSize: false,
    executionMode: false,
    tradability: false,
    spread: false,
    marginRequirements: false,
    swaps: false,
    commission: false,
    tradingAmount: false
  });

  // Section enabled state (checkboxes)
  const [enabledSections, setEnabledSections] = useState({
    contractSize: false,
    executionMode: false,
    tradability: false,
    spread: false,
    marginRequirements: false,
    swaps: false,
    commission: false,
    tradingAmount: false
  });

  // Form data
  const [formData, setFormData] = useState({
    // Contract Size - affects P&L calculations
    contractSize: firstInst?.contractSize || 100000,
    quoteCurrency: firstInst?.quoteCurrency || '',
    // Execution Mode
    executionMode: firstInst?.executionMode || "Market",
    // Tradability
    tradability: firstInst?.tradability || "Full Trade",
    // Spread
    spreadType: firstInst?.spreadType || "Variable",
    spreadMarkUp: firstInst?.spreadMarkUp ?? 0,
    shiftAsk: firstInst?.shiftAsk ?? 0,
    shiftBid: firstInst?.shiftBid ?? 0,
    // Margin Requirements - Now using leverage (1:X format)
    baseLeverage: firstInst?.leverage || 100,
    marginCoefficient: firstInst?.marginCoefficient ?? 1,
    // Swaps
    swapType: firstInst?.swapType || "Default",
    swapMarkupShort: firstInst?.swapMarkupShort ?? 0,
    swapMarkupLong: firstInst?.swapMarkupLong ?? 0,
    finalSwapShort: 0,
    finalSwapLong: 0,
    pipSize: firstInst?.pipSize || '',
    swapDays: firstInst?.swapDays?.join(',') ?? '',
    tripleSwapDay: firstInst?.tripleSwapDay ?? '',
    // Commission
    commissionType: firstInst?.commissionType || "%",
    commissionShort: firstInst?.commissionShort ?? 0,
    commissionLong: firstInst?.commissionLong ?? 0,
    // Trading Amount
    minTradingAmount: firstInst?.minTradingAmount ?? 0.01,
    maxTradingAmount: firstInst?.maxTradingAmount ?? 100
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleEnabled = (section) => {
    setEnabledSections(prev => ({ ...prev, [section]: !prev[section] }));
    if (!enabledSections[section]) setExpandedSections(prev => ({ ...prev, [section]: true }));
  };
  const sectionControls = { expandedSections, enabledSections, onToggle: toggleSection, onToggleEnabled: toggleEnabled };

  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (saving) return;
    const updates = {};
    if (enabledSections.contractSize) {
      updates.contractSize = formData.contractSize;
      updates.quoteCurrency=formData.quoteCurrency?formData.quoteCurrency.toUpperCase():null;
    }
    if (enabledSections.executionMode) updates.executionMode = formData.executionMode;
    if (enabledSections.tradability) updates.tradability = formData.tradability;
    if (enabledSections.spread) {
      updates.spreadType = formData.spreadType;
      updates.spreadMarkUp = formData.spreadMarkUp;
      updates.shiftAsk = formData.shiftAsk;
      updates.shiftBid = formData.shiftBid;
    }
    if (enabledSections.marginRequirements) {
      updates.baseLeverage = formData.baseLeverage;
      if (!scoped) updates.marginCoefficient = formData.marginCoefficient;
    }
    if (enabledSections.swaps) {
      updates.swapType = formData.swapType;
      updates.swapMarkupShort = formData.swapMarkupShort;
      updates.swapMarkupLong = formData.swapMarkupLong;
      updates.pipSize=formData.pipSize?Number(formData.pipSize):null;
      updates.swapDays=formData.swapDays.trim()?formData.swapDays.split(',').map(value=>Number(value.trim())):null;
      updates.tripleSwapDay=formData.tripleSwapDay!==''?Number(formData.tripleSwapDay):null;
    }
    if (enabledSections.commission) {
      updates.commissionType = formData.commissionType;
      updates.commissionShort = formData.commissionShort;
      updates.commissionLong = formData.commissionLong;
    }
    if (enabledSections.tradingAmount) {
      updates.minTradingAmount = formData.minTradingAmount;
      updates.maxTradingAmount = formData.maxTradingAmount;
    }
    
    console.log('EditInstrumentsPolicyModal handleSave - enabledSections:', enabledSections);
    console.log('EditInstrumentsPolicyModal handleSave - formData.contractSize:', formData.contractSize);
    console.log('EditInstrumentsPolicyModal handleSave - updates:', updates);
    console.log('EditInstrumentsPolicyModal handleSave - instruments:', instruments);
    
    if (!Object.keys(updates).length) { toast.error('En az bir politika seçin.'); return; }
    setSaving(true);
    try { await onSave({ instruments, updates }); onClose(); }
    catch (error) { const detail=error.response?.data?.detail;toast.error(typeof detail==='string'?detail:Array.isArray(detail)?detail.map(item=>`${item.loc?.slice(1).join('.')}: ${item.msg}`).join('; '):'Politika kaydedilemedi.'); }
    finally { setSaving(false); }
  };

  return (
    <div ref={controlRoot} className="instrument-policy-overlay" onClick={saving ? undefined : onClose} data-testid="instrument-policy-dialog">
      <div className="instrument-policy-shell" role="dialog" aria-modal="true" aria-labelledby="instrument-policy-title" data-testid="instrument-policy-shell" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="instrument-policy-header">
          <h2 id="instrument-policy-title" data-testid="instrument-policy-title">
            {isBulk ? `Toplu Düzenle — ${instruments.length} Enstrüman` : 'Enstrüman Politikalarını Düzenle'}
          </h2>
          <button type="button" data-testid="instrument-policy-close" aria-label="Kapat" disabled={saving} onClick={onClose}><X size={18} /></button>
        </div>

        {/* Info banner for bulk edit */}
        {isBulk && (
          <div className="instrument-policy-bulk" data-testid="instrument-policy-selection-summary">
            <p>
              <strong>{instruments.length}</strong> enstrüman seçili. Yalnızca işaretlenen bölümler güncellenecek.
            </p>
          </div>
        )}

        {/* Scrollable content */}
        <div className="instrument-policy-scroll" data-testid="instrument-policy-scroll">
          {/* Contract Size - NEW SECTION */}
          <PolicySection {...sectionControls} id="contractSize" title="Kontrat Büyüklüğü">
            <div className="space-y-4">
              <label className="flex items-center gap-4 text-sm text-gray-600" htmlFor="instrument-quote-currency"><span className="w-40">Fiyat Para Birimi</span><input id="instrument-quote-currency" data-testid="instrument-quote-currency" className="sirix-form-input flex-1 min-w-0" maxLength={5} value={formData.quoteCurrency} onChange={event=>setFormData({...formData,quoteCurrency:event.target.value.toUpperCase()})}/></label>
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm text-gray-600">Kontrat Büyüklüğü:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.contractSize}
                  onChange={(e) => setFormData({...formData, contractSize: parseFloat(e.target.value) || 1})}
                  step="1"
                  min="1"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                <p className="text-sm text-blue-800 font-medium mb-2">Kontrat Büyüklükleri:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <strong>Döviz Çiftleri:</strong> 100.000 (1 lot = 100.000 birim)</li>
                  <li>• <strong>XAUUSD (Altın):</strong> 100 (1 lot = 100 ons)</li>
                  <li>• <strong>XAGUSD (Gümüş):</strong> 5.000 (1 lot = 5.000 ons)</li>
                  <li>• <strong>Kripto (BTC, ETH vb.):</strong> 1 (1 lot = 1 coin)</li>
                  <li>• <strong>Endeksler (US100, US500 vb.):</strong> 1 (1 lot = 1 kontrat)</li>
                  <li>• <strong>Türk Hisseleri (BIST):</strong> 1 (1 lot = 1 hisse)</li>
                  <li>• <strong>ABD Hisseleri:</strong> 1 (1 lot = 1 hisse)</li>
                </ul>
              </div>
              <p className="text-sm text-gray-500 italic mt-2">
                0,01 lot = {(0.01 * formData.contractSize).toLocaleString('tr-TR')} birim
              </p>
            </div>
          </PolicySection>

          {/* Execution Mode */}
          <PolicySection {...sectionControls} id="executionMode" title="Gerçekleşme Türü">
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-600">Tür:</label>
              <select 
                className="sirix-form-select flex-1"
                value={formData.executionMode}
                onChange={(e) => setFormData({...formData, executionMode: e.target.value})}
              >
                <option value="Market">Piyasa</option>
                <option value="Instant">Anında</option>
                <option value="Exchange" disabled={scoped}>{scoped ? 'Borsa — bağlı değil' : 'Borsa'}</option>
              </select>
            </div>
          </PolicySection>

          {/* Tradability */}
          <PolicySection {...sectionControls} id="tradability" title="İşlem Yetkisi">
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-600">İşlem Yetkisi:</label>
              <select 
                className="sirix-form-select flex-1"
                value={formData.tradability}
                onChange={(e) => setFormData({...formData, tradability: e.target.value})}
              >
                <option value="Full Trade">Tam İşlem</option>
                <option value="Close Only">Yalnızca Kapat</option>
                <option value="Disabled">Kapalı</option>
                <option value="Long Only">Yalnızca Alış</option>
                <option value="Short Only">Yalnızca Satış</option>
              </select>
            </div>
          </PolicySection>

          {/* Spread */}
          <PolicySection {...sectionControls} id="spread" title="Alış-Satış Farkı">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Fark Türü:</label>
                <select 
                  className="sirix-form-select flex-1"
                  value={formData.spreadType}
                  onChange={(e) => setFormData({...formData, spreadType: e.target.value})}
                >
                  <option value="Variable">Değişken</option>
                  <option value="Fixed">Sabit</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Ek Fark:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.spreadMarkUp}
                  onChange={(e) => setFormData({...formData, spreadMarkUp: parseFloat(e.target.value)})}
                  step="0.1"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Alış Kaydırma:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.shiftAsk}
                  onChange={(e) => setFormData({...formData, shiftAsk: parseFloat(e.target.value)})}
                  step="0.1"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Satış Kaydırma:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.shiftBid}
                  onChange={(e) => setFormData({...formData, shiftBid: parseFloat(e.target.value)})}
                  step="0.1"
                />
              </div>
            </div>
          </PolicySection>

          {/* Margin Requirements */}
          <PolicySection {...sectionControls} id="marginRequirements" title="Kaldıraç">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm text-gray-600">Kaldıraç:</label>
                <div className="flex-1">
                  <LeverageSelector
                    value={formData.baseLeverage}
                    onChange={(val) => {
                      setEnabledSections(prev => ({...prev, marginRequirements: true}));
                      console.log('PARENT: onChange received:', val);
                      const newFormData = {...formData, baseLeverage: val};
                      console.log('PARENT: newFormData:', newFormData);
                      setFormData(newFormData);
                    }}
                    showInfo={false}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm text-gray-600">Teminat Oranı:</label>
                <div className="flex items-center gap-2 flex-1">
                  <LeverageBadge value={formData.baseLeverage} />
                  <button 
                    className="ml-auto text-gray-400 hover:text-gray-600"
                    onClick={() => setFormData({...formData, baseLeverage: 1})}
                  >×</button>
                </div>
              </div>
              <p className="text-sm text-gray-500 italic mt-4">
                Enstrüman kaldıracı işlem koşulunun varsayılanını geçersiz kılar. Açık pozisyonların teminatı yeniden hesaplanır; zorunlu kapatma tetiklenebilir.
              </p>
            </div>
          </PolicySection>

          {/* Swaps */}
          <PolicySection {...sectionControls} id="swaps" title="Gecelik Faiz">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4"><label className="text-sm min-w-0" htmlFor="instrument-pip-size">Pip Boyutu<input id="instrument-pip-size" data-testid="instrument-pip-size" type="number" min="0.00000001" step="any" className="sirix-form-input w-full mt-2" value={formData.pipSize} onChange={event=>setFormData({...formData,pipSize:event.target.value})}/></label><label className="text-sm min-w-0" htmlFor="instrument-swap-days">Rollover Günleri<input id="instrument-swap-days" data-testid="instrument-swap-days" className="sirix-form-input w-full mt-2" placeholder="0,1,2,3,4" value={formData.swapDays} onChange={event=>setFormData({...formData,swapDays:event.target.value})}/></label><label className="text-sm min-w-0" htmlFor="instrument-triple-day">Üçlü Swap Günü<select id="instrument-triple-day" data-testid="instrument-triple-day" className="sirix-form-select w-full mt-2" value={formData.tripleSwapDay} onChange={event=>setFormData({...formData,tripleSwapDay:event.target.value})}><option value="">Varsayılan</option><option value="-1">Yok</option>{['Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi','Pazar'].map((label,index)=><option key={index} value={index}>{label}</option>)}</select></label></div>
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Faiz Türü:</label>
                <select 
                  className="sirix-form-select"
                  style={{ width: 150 }}
                  value={formData.swapType}
                  onChange={(e) => setFormData({...formData, swapType: e.target.value})}
                >
                  <option value="Default">Varsayılan</option>
                  <option value="Pips">Pip</option>
                  <option value="Percent">Yüzde</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mt-4">
                {/* Short */}
                <div>
                  <h4 className="text-[#00BCD4] font-medium mb-4">Satış</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="w-28 text-sm text-gray-600">Gecelik Faiz:</label>
                      <input 
                        type="number" 
                        className="sirix-form-input flex-1"
                        value={formData.swapMarkupShort}
                        onChange={(e) => setFormData({...formData, swapMarkupShort: parseFloat(e.target.value)})}
                        step="0.001"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="w-28 text-sm text-gray-600">Son Faiz:</label>
                      <div className="flex items-center gap-2 flex-1">
                        <Info size={16} className="text-blue-500" />
                        <span className="font-mono" data-testid="policy-final-swap-short">{formData.swapMarkupShort}</span>
                        <button className="ml-auto text-gray-400 hover:text-gray-600" data-testid="policy-reset-swap-short" onClick={()=>setFormData({...formData,swapMarkupShort:0})}>×</button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Long */}
                <div>
                  <h4 className="text-[#00BCD4] font-medium mb-4">Alış</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="w-28 text-sm text-gray-600">Gecelik Faiz:</label>
                      <input 
                        type="number" 
                        className="sirix-form-input flex-1"
                        value={formData.swapMarkupLong}
                        onChange={(e) => setFormData({...formData, swapMarkupLong: parseFloat(e.target.value)})}
                        step="0.001"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="w-28 text-sm text-gray-600">Son Faiz:</label>
                      <div className="flex items-center gap-2 flex-1">
                        <Info size={16} className="text-blue-500" />
                        <span className="font-mono" data-testid="policy-final-swap-long">{formData.swapMarkupLong}</span>
                        <button className="ml-auto text-gray-400 hover:text-gray-600" data-testid="policy-reset-swap-long" onClick={()=>setFormData({...formData,swapMarkupLong:0})}>×</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PolicySection>

          {/* Commission */}
          <PolicySection {...sectionControls} id="commission" title="Komisyon">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Komisyon Türü:</label>
                <select 
                  className="sirix-form-select"
                  style={{ width: 150 }}
                  value={formData.commissionType}
                  onChange={(e) => setFormData({...formData, commissionType: e.target.value})}
                >
                  <option value="%">%</option>
                  <option value="Fixed">Sabit</option>
                  <option value="Per Lot">Lot Başına</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="w-32 text-sm text-gray-600">Satış:</label>
                  <input 
                    type="number" 
                    className="sirix-form-input flex-1"
                    value={formData.commissionShort}
                    onChange={(e) => setFormData({...formData, commissionShort: parseFloat(e.target.value)})}
                    step="0.01"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="w-32 text-sm text-gray-600">Alış:</label>
                  <input 
                    type="number" 
                    className="sirix-form-input flex-1"
                    value={formData.commissionLong}
                    onChange={(e) => setFormData({...formData, commissionLong: parseFloat(e.target.value)})}
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </PolicySection>

          {/* Trading Amount */}
          <PolicySection {...sectionControls} id="tradingAmount" title="İşlem Miktarı">
            <div className="grid grid-cols-2 gap-8">
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Min. Miktar:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.minTradingAmount}
                  onChange={(e) => setFormData({...formData, minTradingAmount: parseFloat(e.target.value)})}
                  step="0.01"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-32 text-sm text-gray-600">Maks. Miktar:</label>
                <input 
                  type="number" 
                  className="sirix-form-input flex-1"
                  value={formData.maxTradingAmount}
                  onChange={(e) => setFormData({...formData, maxTradingAmount: parseFloat(e.target.value)})}
                  step="1"
                />
              </div>
            </div>
          </PolicySection>
        </div>

        {/* Footer */}
        <div className="instrument-policy-footer">
          <button 
            className="instrument-policy-confirm"
            onClick={handleSave}
            data-testid="edit-policy-ok-btn"
            disabled={saving || !Object.values(enabledSections).some(Boolean)}
          >
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          <button 
            className="instrument-policy-cancel"
            data-testid="instrument-policy-cancel"
            disabled={saving}
            onClick={onClose}
          >
            ✗ Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Instrument Modal - for adding existing or virtual instruments
const AddInstrumentModal = ({ onClose, onAdd, existingInstruments }) => {
  const [mode, setMode] = useState("existing"); // "existing" or "virtual"
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [search, setSearch] = useState("");
  
  // Virtual instrument form
  const [virtualInstrument, setVirtualInstrument] = useState({
    symbol: "",
    name: "",
    group: "Virtual",
    basePrice: 100,
    volatility: 5, // % daily movement
    trend: "neutral", // up, down, neutral
    currency: "USD"
  });

  // Mock available instruments that can be added
  const availableInstruments = [
    { symbol: "AAPL", name: "Apple Inc.", group: "US Stocks" },
    { symbol: "GOOGL", name: "Alphabet Inc.", group: "US Stocks" },
    { symbol: "MSFT", name: "Microsoft Corp.", group: "US Stocks" },
    { symbol: "TSLA", name: "Tesla Inc.", group: "US Stocks" },
    { symbol: "NVDA", name: "NVIDIA Corp.", group: "US Stocks" },
    { symbol: "THYAO.IS", name: "Turkish Airlines", group: "BIST" },
    { symbol: "GARAN.IS", name: "Garanti Bank", group: "BIST" },
    { symbol: "AKBNK.IS", name: "Akbank", group: "BIST" },
    { symbol: "BTCUSD", name: "Bitcoin", group: "Crypto" },
    { symbol: "ETHUSD", name: "Ethereum", group: "Crypto" },
    { symbol: "XRPUSD", name: "Ripple", group: "Crypto" },
    { symbol: "XAUUSD", name: "Gold", group: "Commodities" },
    { symbol: "XAGUSD", name: "Silver", group: "Commodities" },
  ].filter(i => !existingInstruments.some(e => e.symbol === i.symbol));

  const filteredInstruments = availableInstruments.filter(i =>
    i.symbol.toLowerCase().includes(search.toLowerCase()) ||
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.group.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (symbol) => {
    setSelectedInstruments(prev => 
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  };

  const handleAdd = () => {
    if (mode === "existing") {
      const toAdd = availableInstruments.filter(i => selectedInstruments.includes(i.symbol));
      onAdd({ mode: "existing", instruments: toAdd });
    } else {
      if (!virtualInstrument.symbol || !virtualInstrument.name) {
        toast.error("Please fill in symbol and name");
        return;
      }
      onAdd({ mode: "virtual", instrument: virtualInstrument });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="text-xl text-[#00BCD4]">Add Instruments</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={onClose}>×</button>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b shrink-0">
          <button
            className={`px-6 py-3 font-medium ${mode === "existing" ? "text-[#00BCD4] border-b-2 border-[#00BCD4]" : "text-gray-500"}`}
            onClick={() => setMode("existing")}
          >
            Existing Instruments
          </button>
          <button
            className={`px-6 py-3 font-medium ${mode === "virtual" ? "text-[#00BCD4] border-b-2 border-[#00BCD4]" : "text-gray-500"}`}
            onClick={() => setMode("virtual")}
          >
            Create Virtual Instrument
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {mode === "existing" ? (
            <>
              <div className="sirix-search mb-4">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search instruments..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              
              <div className="border rounded max-h-80 overflow-y-auto">
                <table className="sirix-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Symbol</th>
                      <th>Name</th>
                      <th>Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInstruments.map(inst => (
                      <tr 
                        key={inst.symbol}
                        className={selectedInstruments.includes(inst.symbol) ? "bg-[#00BCD4]/10" : ""}
                        onClick={() => toggleSelect(inst.symbol)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <input 
                            type="checkbox" 
                            checked={selectedInstruments.includes(inst.symbol)}
                            onChange={() => {}}
                          />
                        </td>
                        <td className="text-[#00BCD4] font-semibold">{inst.symbol}</td>
                        <td>{inst.name}</td>
                        <td>{inst.group}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <p className="text-sm text-gray-500 mt-3">
                Selected: {selectedInstruments.length} instrument(s)
              </p>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Create a virtual instrument with simulated price movements. The instrument will have an auto-generated chart based on your settings.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Symbol *</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    placeholder="e.g., VIRTUAL1"
                    value={virtualInstrument.symbol}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, symbol: e.target.value.toUpperCase()})}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Name *</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    placeholder="e.g., Virtual Asset 1"
                    value={virtualInstrument.name}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, name: e.target.value})}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Group</label>
                  <select
                    className="sirix-form-select"
                    value={virtualInstrument.group}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, group: e.target.value})}
                  >
                    <option value="Virtual">Virtual</option>
                    <option value="Custom">Custom</option>
                    <option value="Synthetic">Synthetic</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Base Currency</label>
                  <select
                    className="sirix-form-select"
                    value={virtualInstrument.currency}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, currency: e.target.value})}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Designer Base Price</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={virtualInstrument.basePrice}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, basePrice: parseFloat(e.target.value)})}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Daily Volatility (%)</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={virtualInstrument.volatility}
                    onChange={(e) => setVirtualInstrument({...virtualInstrument, volatility: parseFloat(e.target.value)})}
                    step="0.1"
                    min="0"
                    max="50"
                  />
                </div>
                <div className="sirix-form-group col-span-2">
                  <label className="sirix-form-label">Price Trend</label>
                  <div className="flex gap-4 mt-2">
                    {["up", "neutral", "down"].map(trend => (
                      <label key={trend} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="trend"
                          checked={virtualInstrument.trend === trend}
                          onChange={() => setVirtualInstrument({...virtualInstrument, trend})}
                        />
                        <span className={`capitalize ${trend === "up" ? "text-green-600" : trend === "down" ? "text-red-600" : "text-gray-600"}`}>
                          {trend === "up" ? "↑ Bullish" : trend === "down" ? "↓ Bearish" : "→ Neutral"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded mt-4">
                <h4 className="font-medium text-gray-700 mb-2">Preview</h4>
                <p className="text-sm text-gray-600">
                  <strong>{virtualInstrument.symbol || "SYMBOL"}</strong> - {virtualInstrument.name || "Name"}
                  <br />
                  Starting at <strong>{virtualInstrument.currency} {virtualInstrument.basePrice}</strong> with 
                  <strong> {virtualInstrument.volatility}%</strong> daily volatility and 
                  <strong> {virtualInstrument.trend}</strong> trend bias.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-4 px-6 py-5 border-t bg-gray-50 shrink-0">
          <button 
            className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium"
            onClick={handleAdd}
            disabled={mode === "existing" && selectedInstruments.length === 0}
          >
            + Add {mode === "existing" && selectedInstruments.length > 0 ? `(${selectedInstruments.length})` : ""}
          </button>
          <button 
            className="px-8 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded transition-colors font-medium"
            onClick={onClose}
          >
            ✗ Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const TradingTermsModal = ({ term, onClose, scoped = false, onSaveTerm, instrumentsContent, canEdit = true }) => {
  const controlRoot=useRef(null);useLegacyControlIds(controlRoot,'trading-term');
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    tradingPolicies: true,
    executionPolicies: true
  });
  const [search, setSearch] = useState("");
  const [showAddInstrument, setShowAddInstrument] = useState(false);
  const [showEditPolicy, setShowEditPolicy] = useState(false);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Virtual Price Controller state
  const [showVirtualPriceController, setShowVirtualPriceController] = useState(false);
  const [selectedInstrumentForPrice, setSelectedInstrumentForPrice] = useState(null);
  
  // General Settings state
  const [generalSettings, setGeneralSettings] = useState({
    name: term?.name || "Default TRY",
    broker: term?.settings?.broker_name || "ZipGlobal",
    lastUpdate: new Date(term?.updated_at || Date.now()).toLocaleString('tr-TR', { 
      day: '2-digit', month: '2-digit', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    }).replace(',', ''),
    comment: term?.comment || ""
  });
  const [generalSettingsSaving, setGeneralSettingsSaving] = useState(false);

  // Available brokers for dropdown
  const brokerOptions = [...new Set([generalSettings.broker, "ZipGlobal", "E1 Broker", "SiRiX Pro", "MetaTrader", "Özel"])];
  
  // Trading Settings state - synced with backend
  const [tradingSettings, setTradingSettings] = useState({
    marginCallLevel: 70.0,
    stopOutLevel: 30.0,
    forceStopLoss: false,
    forexBaseLeverage: 100,
    cfdsMarginCoefficient: 1.0,
    netMaxExposureEnabled: false,
    maxExposureUsd: 0,
    commissionType: "%",
    commissionSize: 0,
    isIslamic: false,
    strongHedgedMarginMode: false
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  
  // Load trading settings on mount
  useEffect(() => {
    if (scoped) { setTradingSettings(settingsToLegacy(term.settings || {})); setSettingsLoading(false); return; }
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trading-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTradingSettings({
            marginCallLevel: data.margin_call_level || 70.0,
            stopOutLevel: data.stop_out_level || 30.0,
            forceStopLoss: data.force_stop_loss || false,
            forexBaseLeverage: data.forex_base_leverage || 100,
            cfdsMarginCoefficient: data.cfds_margin_coefficient || 1.0,
            netMaxExposureEnabled: data.net_max_exposure_enabled || false,
            maxExposureUsd: data.max_exposure_usd || 0,
            commissionType: data.commission_type || "%",
            commissionSize: data.commission_size || 0,
            isIslamic: data.is_islamic || false,
            strongHedgedMarginMode: data.strong_hedged_margin_mode || false
          });
        }
      } catch (error) {
        console.error('Error loading trading settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();
  }, []);
  
  // Save trading settings
  const saveTradingSettings = async () => {
    setSettingsSaving(true);
    try {
      if (scoped) {
        await onSaveTerm({name:generalSettings.name,currency:term.currency,comment:generalSettings.comment,settings:{...settingsFromLegacy(tradingSettings),broker_name:generalSettings.broker}});
        toast.success('İşlem koşulları kaydedildi.');
        return;
      }
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trading-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          margin_call_level: tradingSettings.marginCallLevel,
          stop_out_level: tradingSettings.stopOutLevel,
          force_stop_loss: tradingSettings.forceStopLoss,
          forex_base_leverage: tradingSettings.forexBaseLeverage,
          cfds_margin_coefficient: tradingSettings.cfdsMarginCoefficient,
          net_max_exposure_enabled: tradingSettings.netMaxExposureEnabled,
          max_exposure_usd: tradingSettings.maxExposureUsd,
          commission_type: tradingSettings.commissionType,
          commission_size: tradingSettings.commissionSize,
          is_islamic: tradingSettings.isIslamic,
          strong_hedged_margin_mode: tradingSettings.strongHedgedMarginMode
        })
      });
      if (response.ok) {
        toast.success('İşlem ayarları kaydedildi.');
      } else {
        toast.error('İşlem ayarları kaydedilemedi.');
      }
    } catch (error) {
      console.error('Error saving trading settings:', error);
      toast.error(error.response?.data?.detail || error.message || 'İşlem ayarları kaydedilemedi.');
    } finally {
      setSettingsSaving(false);
    }
  };
  
  // Alias for backward compatibility
  const forexBaseLeverage = tradingSettings.forexBaseLeverage;
  const setForexBaseLeverage = (value) => setTradingSettings(prev => ({ ...prev, forexBaseLeverage: value }));

  // All instruments loaded from API
  const [localInstruments, setLocalInstruments] = useState([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(true);

  // Helper to map category to group name and settings
  const getCategoryConfig = (category) => {
    const configs = {
      forex: { group: "Major", leverage: 100, spread: 5, commission: "0%", tradingAmount: "0.01-100", decimals: 5, contractSize: 100000 },
      crypto: { group: "Crypto Currency", leverage: 5, spread: 50, commission: "0.1%", tradingAmount: "0.001-10", decimals: 2, contractSize: 1 },
      commodities: { group: "Commodities", leverage: 20, spread: 30, commission: "0%", tradingAmount: "0.01-50", decimals: 2, contractSize: 100 },
      indices: { group: "Indices", leverage: 50, spread: 10, commission: "0%", tradingAmount: "0.1-50", decimals: 2, contractSize: 1 },
      stocks: { group: "US Stocks", leverage: 10, spread: 5, commission: "0%", tradingAmount: "1-500", decimals: 2, contractSize: 1 },
      bist: { group: "BIST", leverage: 10, spread: 8, commission: "0.1%", tradingAmount: "1-1000", decimals: 2, contractSize: 1 },
    };
    return configs[category?.toLowerCase()] || configs.forex;
  };

  // Get contract size based on symbol
  const getContractSizeForSymbol = (symbol, category) => {
    // Special cases for specific symbols
    if (symbol === 'XAUUSD') return 100;       // Gold: 100 oz
    if (symbol === 'XAGUSD') return 5000;      // Silver: 5000 oz
    if (symbol?.includes('/USD') || symbol?.endsWith('USD')) {
      // Check if it's forex
      if (category === 'forex') return 100000;
    }
    // Otherwise use category default
    const config = getCategoryConfig(category);
    return config.contractSize;
  };

  // Load ALL instruments from API on mount
  useEffect(() => {
    if (scoped) { setInstrumentsLoading(false); return; }
    const loadAllInstruments = async () => {
      try {
        setInstrumentsLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instruments?limit=2000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Transform API data to trading terms format
          const transformedInstruments = data.map(inst => {
            const config = getCategoryConfig(inst.category);
            const basePrice = inst.category === 'bist' ? 100 : 
                            inst.category === 'crypto' ? 50000 : 
                            inst.category === 'forex' ? 1.1 : 100;
            // Use stored contract_size from API or calculate based on symbol/category
            const contractSize = inst.contract_size || getContractSizeForSymbol(inst.symbol, inst.category);
            return {
              symbol: inst.symbol,
              name: inst.name,
              group: config.group,
              category: inst.category,
              units: 1,
              contractSize: contractSize,
              executionMode: "Market",
              tradability: "Full Trade",
              spread: `Variable: ${config.spread}`,
              leverage: inst.leverage || config.leverage,
              commission: config.commission,
              swapShort: inst.category === 'bist' ? -0.04 : inst.category === 'crypto' ? -5 : 0,
              swapLong: inst.category === 'bist' ? -0.02 : inst.category === 'crypto' ? -5 : 0,
              tradingAmount: config.tradingAmount,
              edited: false,
              livePrice: basePrice,
              virtualPrice: basePrice,
              virtualPriceActive: false,
              decimals: config.decimals
            };
          });
          setLocalInstruments(transformedInstruments);
          console.log(`Loaded ${transformedInstruments.length} instruments for Trading Terms`);
        }
      } catch (error) {
        console.error('Error loading instruments:', error);
        // Fallback to some defaults if API fails
        setLocalInstruments([
          { symbol: "EURUSD", group: "Major", category: "forex", units: 1, contractSize: 100000, executionMode: "Market", tradability: "Full Trade", spread: "Variable: 5", leverage: 100, commission: "0%", swapShort: 0, swapLong: 0, tradingAmount: "0.01-100", edited: false, livePrice: 1.0876, virtualPrice: 1.0876, virtualPriceActive: false, decimals: 5 },
          { symbol: "BTCUSD", group: "Crypto Currency", category: "crypto", units: 1, contractSize: 1, executionMode: "Market", tradability: "Full Trade", spread: "Variable: 50", leverage: 5, commission: "0.1%", swapShort: -5, swapLong: -5, tradingAmount: "0.001-10", edited: false, livePrice: 67245.50, virtualPrice: 67245.50, virtualPriceActive: false, decimals: 2 },
          { symbol: "XAUUSD", group: "Commodities", category: "commodities", units: 1, contractSize: 100, executionMode: "Market", tradability: "Full Trade", spread: "Variable: 30", leverage: 20, commission: "0%", swapShort: -2, swapLong: -1, tradingAmount: "0.01-50", edited: false, livePrice: 2178.45, virtualPrice: 2178.45, virtualPriceActive: false, decimals: 2 },
        ]);
      } finally {
        setInstrumentsLoading(false);
      }
    };
    loadAllInstruments();
  }, []);

  // Open Virtual Price Controller for an instrument
  const handleOpenVirtualPriceController = (instrument) => {
    setSelectedInstrumentForPrice(instrument);
    setShowVirtualPriceController(true);
  };

  // Apply virtual price settings
  const handleApplyVirtualPrice = (priceData) => {
    setLocalInstruments(prev => prev.map(inst => {
      if (inst.symbol === priceData.symbol) {
        return {
          ...inst,
          virtualPrice: priceData.virtualPrice,
          virtualPriceActive: priceData.isActive,
          targetPrice: priceData.targetPrice,
          priceDuration: priceData.duration,
          priceStartTime: priceData.startTime,
          priceStartValue: priceData.startPrice
        };
      }
      return inst;
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Sorting logic
  const sortedInstruments = useMemo(() => {
    let sorted = [...localInstruments];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle string values
        if (typeof aVal === 'string') {
          // Extract number from strings like "Variable: 5" or "0.01-100"
          if (sortConfig.key === 'spread') {
            aVal = parseFloat(aVal.replace(/[^\d.]/g, '')) || 0;
            bVal = parseFloat(bVal.replace(/[^\d.]/g, '')) || 0;
          } else if (sortConfig.key === 'tradingAmount') {
            aVal = parseFloat(aVal.split('-')[0]) || 0;
            bVal = parseFloat(bVal.split('-')[0]) || 0;
          } else if (sortConfig.key === 'margin' || sortConfig.key === 'commission') {
            aVal = parseFloat(aVal) || 0;
            bVal = parseFloat(bVal) || 0;
          } else {
            aVal = aVal.toLowerCase();
            bVal = bVal?.toLowerCase() || '';
          }
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [localInstruments, sortConfig]);

  // Filtering
  const filteredInstruments = sortedInstruments.filter(i =>
    i.symbol.toLowerCase().includes(search.toLowerCase()) ||
    i.group.toLowerCase().includes(search.toLowerCase())
  );

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Toggle instrument selection
  const toggleSelectInstrument = (symbol, e) => {
    if (e) e.stopPropagation();
    setSelectedInstruments(prev =>
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  };

  // Handle bulk edit
  const handleBulkEdit = () => {
    if (selectedInstruments.length === 0) {
      toast.error("No instruments selected");
      return;
    }
    setShowEditPolicy(true);
  };

  // Handle edit save - Actually apply changes to instruments
  const handleEditSave = ({ instruments: editedInstrumentsList, updates }) => {
    console.log('handleEditSave called with:', { editedInstrumentsList, updates });
    
    // Get the symbols of edited instruments
    const editedSymbols = editedInstrumentsList.map(i => i.symbol);
    
    // Update the local instruments with the new values
    setLocalInstruments(prev => {
      const newInstruments = prev.map(inst => {
        if (editedSymbols.includes(inst.symbol)) {
          const updatedInst = { ...inst, edited: true };
          
          // Apply each update
          if (updates.executionMode !== undefined) updatedInst.executionMode = updates.executionMode;
          if (updates.contractSize !== undefined) {
            console.log(`Updating ${inst.symbol} contractSize from ${inst.contractSize} to ${updates.contractSize}`);
            updatedInst.contractSize = updates.contractSize;
          }
          if (updates.tradability !== undefined) updatedInst.tradability = updates.tradability;
          if (updates.baseLeverage !== undefined) {
            console.log(`Updating ${inst.symbol} leverage from ${inst.leverage} to ${updates.baseLeverage}`);
            updatedInst.leverage = updates.baseLeverage;
          }
          if (updates.spreadMarkUp !== undefined) {
            updatedInst.spread = `${updates.spreadType || 'Variable'}: ${updates.spreadMarkUp}`;
          }
          if (updates.swapMarkupShort !== undefined) updatedInst.swapShort = updates.swapMarkupShort;
          if (updates.swapMarkupLong !== undefined) updatedInst.swapLong = updates.swapMarkupLong;
          if (updates.commissionShort !== undefined || updates.commissionLong !== undefined) {
            const commValue = updates.commissionShort || updates.commissionLong || 0;
            updatedInst.commission = `${commValue}${updates.commissionType || '%'}`;
          }
          if (updates.minTradingAmount !== undefined || updates.maxTradingAmount !== undefined) {
            const min = updates.minTradingAmount ?? inst.tradingAmount.split('-')[0];
            const max = updates.maxTradingAmount ?? inst.tradingAmount.split('-')[1];
            updatedInst.tradingAmount = `${min}-${max}`;
          }
          
          return updatedInst;
        }
        return inst;
      });
      console.log('Updated instruments:', newInstruments);
      return newInstruments;
    });
    
    toast.success(`Updated ${editedInstrumentsList.length} instrument(s)`);
    setShowEditPolicy(false);
    setSelectedInstruments([]);
  };

  // Handle add instruments
  const handleAddInstruments = async ({ mode, instruments: newInstruments, instrument }) => {
    if (mode === "existing") {
      toast.success(`Added ${newInstruments.length} instrument(s) to trading terms`);
    } else {
      // Create virtual instrument via API
      try {
        const virtualData = {
          symbol: instrument.symbol,
          name: instrument.name,
          type: "forex", // Default type for virtual
          category: instrument.group || "Virtual",
          leverage: 100,
          min_lot_size: 0.01,
          max_lot_size: 100,
          spread: 1.0,
          is_virtual: true,
          base_price: instrument.basePrice,
          volatility: instrument.volatility,
          trend: instrument.trend === "up" ? "up" : instrument.trend === "down" ? "down" : "sideways",
          trend_strength: 50,
          price_seed: Math.floor(Math.random() * 10000)
        };
        
        await instrumentsAPI.create(virtualData);
        toast.success(`Created virtual instrument: ${instrument.symbol}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create virtual instrument");
        return;
      }
    }
    setShowAddInstrument(false);
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronDown size={12} className="opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={12} className="text-[#00BCD4]" /> 
      : <ChevronDown size={12} className="text-[#00BCD4]" />;
  };

  return (
    <div ref={controlRoot} className={`sirix-modal-overlay ${scoped ? 'restored-policy' : ''}`} onClick={onClose} data-testid="trading-terms-dialog">
      <div className="sirix-modal" role="dialog" aria-modal="true" aria-label="İşlem Koşulları" style={{ width: '95%', maxWidth: 1400 }} onClick={(e) => e.stopPropagation()}>
        <div className="sirix-modal-header">
          <h2 className="sirix-modal-title" data-testid="term-dialog-title">İşlem Koşulları — {term.name}</h2>
          <button className="sirix-modal-close" data-testid="trading-terms-close" onClick={onClose}>×</button>
        </div>
        
        <div className="sirix-modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <fieldset disabled={!canEdit}>
          {/* General Section */}
          <div className="sirix-section">
            <div 
              className="sirix-section-header"
              onClick={() => toggleSection('general')}
            >
              Genel
              {expandedSections.general ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            {expandedSections.general && (
              <div className="sirix-section-body">
                {/* First Row: Name, Broker, Last Update */}
                <div className="flex items-center gap-8 mb-6">
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600 whitespace-nowrap">Ad:</label>
                    <input 
                      type="text"
                      className="sirix-form-input"
                      style={{ width: 180 }}
                      value={generalSettings.name}
                      data-testid="term-name"
                      onChange={(e) => setGeneralSettings(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="İşlem Koşulu Adı"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600 whitespace-nowrap">Kurum:</label>
                    <select 
                      className="sirix-form-select"
                      style={{ width: 150 }}
                      value={generalSettings.broker}
                      data-testid="term-broker"
                      onChange={(e) => setGeneralSettings(prev => ({ ...prev, broker: e.target.value }))}
                    >
                      {brokerOptions.map(broker => (
                        <option key={broker} value={broker}>{broker}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600 whitespace-nowrap">Son Güncelleme:</label>
                    <input 
                      type="text"
                      className="sirix-form-input bg-gray-100"
                      style={{ width: 160 }}
                      value={generalSettings.lastUpdate}
                      data-testid="term-last-update"
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                
                {/* Second Row: Comment */}
                <div className="flex items-start gap-3">
                  <label className="text-sm text-gray-600 whitespace-nowrap mt-2">Açıklama:</label>
                  <textarea 
                    className="sirix-form-input flex-1"
                    style={{ minHeight: 100, resize: 'vertical' }}
                    value={generalSettings.comment}
                    data-testid="term-comment"
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="İşlem koşuluna ait notlar…"
                  />
                </div>

                {/* Save Button */}
                <div className="flex justify-end mt-4">
                  <button 
                    className="px-4 py-2 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded font-medium text-sm transition-colors flex items-center gap-2"
                    onClick={async () => {
                      if (scoped) { await saveTradingSettings(); return; }
                      setGeneralSettingsSaving(true);
                      // Update lastUpdate timestamp
                      setGeneralSettings(prev => ({
                        ...prev,
                        lastUpdate: new Date().toLocaleString('en-GB', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        }).replace(',', '')
                      }));
                      setTimeout(() => {
                        setGeneralSettingsSaving(false);
                        toast.success('Genel ayarlar kaydedildi.');
                      }, 500);
                    }}
                    disabled={generalSettingsSaving || settingsSaving}
                    data-testid="term-save-general"
                  >
                    {generalSettingsSaving ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        Kaydediliyor…
                      </>
                    ) : (
                      'Genel Ayarları Kaydet'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Trading Policies Section */}
          <div className="sirix-section">
            <div 
              className="sirix-section-header"
              onClick={() => toggleSection('tradingPolicies')}
            >
              İşlem Politikaları
              {expandedSections.tradingPolicies ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            {expandedSections.tradingPolicies && (
              <div className="sirix-section-body">
                {settingsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="animate-spin text-[#00BCD4]" size={24} />
                    <span className="ml-2 text-gray-500">Ayarlar yükleniyor…</span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-8">
                      {/* Margin Call and Stop Out */}
                      <div>
                        <h4 className="sirix-form-label mb-4">Teminat Çağrısı ve Zorunlu Kapatma</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Teminat Çağrısı:</label>
                            <div className="flex items-center">
                              <input 
                                type="number" 
                                className="sirix-form-input" 
                                style={{ width: 96 }} 
                                value={tradingSettings.marginCallLevel}
                                data-testid="term-margin-call"
                                onChange={(e) => setTradingSettings(prev => ({ ...prev, marginCallLevel: parseFloat(e.target.value) || 0 }))}
                                step="1"
                                min="0"
                                max="10000"
                              />
                              <span className="ml-1 text-sm text-gray-500">%</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Zorunlu Kapatma:</label>
                            <div className="flex items-center">
                              <input 
                                type="number" 
                                className="sirix-form-input" 
                                style={{ width: 96 }} 
                                value={tradingSettings.stopOutLevel}
                                data-testid="term-stop-out"
                                onChange={(e) => setTradingSettings(prev => ({ ...prev, stopOutLevel: parseFloat(e.target.value) || 0 }))}
                                step="1"
                                min="0"
                                max="10000"
                              />
                              <span className="ml-1 text-sm text-gray-500">%</span>
                            </div>
                          </div>
                          <p className="text-xs text-amber-600 mt-2">
                            Teminat seviyesi %{tradingSettings.stopOutLevel} veya altına indiğinde, en yüksek zararlı pozisyondan başlanarak seviye eşik üzerine çıkana kadar sırayla kapatılır. Negatif bakiye silinmez.
                          </p>
                        </div>
                        <h4 className="sirix-form-label mt-6 mb-4">Zarar Durdurma Politikası</h4>
                        <div className="space-y-2">
                          <label className="sirix-checkbox">
                            <input 
                              type="checkbox" 
                              data-testid="term-force-stop-loss"
                              checked={tradingSettings.forceStopLoss}
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, forceStopLoss: e.target.checked }))}
                            />
                            <span className="text-sm">Zarar Durdurma Zorunlu:</span>
                          </label>
                        </div>
                      </div>

                      {/* Margin Requirements */}
                      <div>
                        <h4 className="sirix-form-label mb-4">Teminat Gereksinimleri</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Kaldıraç:</label>
                            <div style={{ width: 140 }}>
                              <LeverageSelector 
                                testId="term-leverage"
                                value={forexBaseLeverage} 
                                onChange={setForexBaseLeverage}
                                showInfo={false}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-amber-600" data-testid="term-leverage-warning">Tüm enstrümanlar için geçerlidir. Kaydedildiğinde açık pozisyonların teminatı yeniden hesaplanır; zorunlu kapatma tetiklenebilir.</p>
                        </div>
                        <div className="mt-6 space-y-2">
                          <label className="sirix-checkbox">
                            <input 
                              type="checkbox" 
                              data-testid="term-net-exposure-enabled"
                              checked={tradingSettings.netMaxExposureEnabled}
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, netMaxExposureEnabled: e.target.checked }))}
                            />
                            <span className="text-sm">Net Risk Sınırı:</span>
                          </label>
                          {tradingSettings.netMaxExposureEnabled && (
                            <div className="flex items-center gap-2 ml-6">
                              <label className="text-sm text-gray-600">Maksimum Risk (USD):</label>
                              <input 
                                type="number" 
                                className="sirix-form-input" 
                                style={{ width: 140 }} 
                                data-testid="term-max-exposure"
                                value={tradingSettings.maxExposureUsd}
                                onChange={(e) => setTradingSettings(prev => ({ ...prev, maxExposureUsd: parseFloat(e.target.value) || 0 }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Commission */}
                      <div>
                        <h4 className="sirix-form-label mb-4">Varsayılan Komisyon</h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Komisyon Türü:</label>
                            <select 
                              className="sirix-form-select" 
                              style={{ width: 140 }}
                              value={tradingSettings.commissionType}
                              data-testid="term-commission-type"
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, commissionType: e.target.value }))}
                            >
                              <option value="%">%</option>
                              <option value="Fixed">Sabit</option>
                               {scoped && <option value="Per Lot">Lot Başına</option>}
                            </select>
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Komisyon Tutarı:</label>
                            <input 
                              type="number" 
                              className="sirix-form-input" 
                              style={{ width: 128 }} 
                              data-testid="term-commission-size"
                              value={tradingSettings.commissionSize}
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, commissionSize: parseFloat(e.target.value) || 0 }))}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="mt-6 space-y-2">
                          <label className="sirix-checkbox">
                            <input 
                              type="checkbox" 
                              checked={tradingSettings.isIslamic}
                              data-testid="term-islamic"
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, isIslamic: e.target.checked }))}
                            />
                            <span className="text-sm">Swapsız Hesap:</span>
                          </label>
                          <label className="flex items-center gap-3 text-sm text-gray-600" htmlFor="term-free-days"><span>Ücretsiz Günler</span><input id="term-free-days" data-testid="term-free-days" className="sirix-form-input min-w-0" style={{width:104}} type="number" min="0" max="3650" disabled={!tradingSettings.isIslamic} value={tradingSettings.freeDays??0} placeholder="0" onChange={event=>setTradingSettings(previous=>({...previous,freeDays:event.target.value===''?'':Number(event.target.value)}))}/></label>
                          <label className="sirix-checkbox">
                            <input 
                              type="checkbox" 
                              checked={tradingSettings.strongHedgedMarginMode}
                              data-testid="term-hedged-margin"
                              onChange={(e) => setTradingSettings(prev => ({ ...prev, strongHedgedMarginMode: e.target.checked }))}
                            />
                            <span className="text-sm">Korunmalı Teminat Modu:</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    {scoped&&<EnginePolicyFields settings={tradingSettings} onChange={setTradingSettings} disabled={settingsSaving}/>}
                    {/* Save Button */}
                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                      <button
                        onClick={saveTradingSettings}
                        data-testid="term-save-trading"
                        disabled={settingsSaving}
                        className="px-6 py-2 bg-[#00BCD4] text-white rounded hover:bg-[#00ACC1] disabled:opacity-50 flex items-center gap-2"
                      >
                        {settingsSaving ? (
                          <>
                            <RefreshCw className="animate-spin" size={16} />
                            Kaydediliyor…
                          </>
                        ) : (
                          'İşlem Ayarlarını Kaydet'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Execution Policies Section */}
          {!scoped && <>
          <div className="sirix-section">
            <div 
              className="sirix-section-header"
              onClick={() => toggleSection('executionPolicies')}
            >
              Execution Policies
              {expandedSections.executionPolicies ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
            {expandedSections.executionPolicies && (
              <div className="sirix-section-body">
                <div className="grid grid-cols-3 gap-8 mb-6">
                  {/* Sleep Time */}
                  <div>
                    <h4 className="sirix-form-label mb-4">Sleep Time</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Standard Sleep Time:</label>
                        <input type="number" className="sirix-form-input" style={{ width: 60 }} defaultValue="0.3" step="0.1" />
                        <span className="text-sm text-gray-500">Sec</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Sleep Time in News Mode:</label>
                        <input type="number" className="sirix-form-input" style={{ width: 60 }} defaultValue="2" />
                        <span className="text-sm text-gray-500">Sec</span>
                      </div>
                    </div>
                  </div>

                  {/* Instant Tolerance */}
                  <div>
                    <h4 className="sirix-form-label mb-4">Instant Tolerance</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Base Tolerance:</label>
                        <input type="number" className="sirix-form-input" style={{ width: 60 }} defaultValue="0.3" step="0.1" />
                        <span className="text-sm text-gray-500">* Spread</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600">Apply for:</span>
                        <label className="sirix-checkbox">
                          <input type="checkbox" defaultChecked />
                          <span className="text-xs">B/S Manual</span>
                        </label>
                        <label className="sirix-checkbox">
                          <input type="checkbox" defaultChecked />
                          <span className="text-xs">B/S Limit</span>
                        </label>
                        <label className="sirix-checkbox">
                          <input type="checkbox" defaultChecked />
                          <span className="text-xs">Take Profit</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Stop Loss Tolerance */}
                  <div>
                    <h4 className="sirix-form-label mb-4">Stop Loss Tolerance</h4>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Base Tolerance:</label>
                      <input type="number" className="sirix-form-input" style={{ width: 60 }} defaultValue="0.5" step="0.1" />
                      <span className="text-sm text-gray-500">* Spread</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instruments Policies - LARGER */}
          </>}
          </fieldset>
          {scoped ? instrumentsContent : <>
          <div className="sirix-section">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="sirix-form-label" style={{ margin: 0 }}>Instruments Policies</span>
                <div className="sirix-search">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="sirix-btn sirix-btn-secondary">
                  <Download size={14} />
                  Export
                </button>
                <button className="sirix-btn sirix-btn-secondary">
                  <Upload size={14} />
                  Import
                </button>
                <button 
                  className="sirix-btn sirix-btn-primary"
                  onClick={() => setShowAddInstrument(true)}
                  data-testid="add-instrument-btn"
                >
                  <Plus size={14} />
                  Add
                </button>
                <button className="sirix-btn sirix-btn-secondary">
                  <Trash2 size={14} />
                  Remove
                </button>
                <button 
                  className="sirix-btn sirix-btn-secondary"
                  onClick={handleBulkEdit}
                  data-testid="bulk-edit-btn"
                >
                  <Edit size={14} />
                  Bulk Edit
                </button>
              </div>
            </div>
            {/* BIGGER TABLE - increased max-h from 64 to 96 (384px) */}
            <div className="overflow-auto" style={{ maxHeight: 400 }}>
              {instrumentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin text-[#00BCD4]" size={24} />
                  <span className="ml-2 text-gray-500">Loading {localInstruments.length > 0 ? localInstruments.length : ''} instruments...</span>
                </div>
              ) : (
              <table className="sirix-table text-xs" data-testid="instruments-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('symbol')}>
                      <div className="flex items-center gap-1">Instrument <SortIcon column="symbol" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('group')}>
                      <div className="flex items-center gap-1">Group <SortIcon column="group" /></div>
                    </th>
                    <th>Units</th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('contractSize')}>
                      <div className="flex items-center gap-1">Contract Size <SortIcon column="contractSize" /></div>
                    </th>
                    <th>Execution Mode</th>
                    <th>Tradability</th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('spread')}>
                      <div className="flex items-center gap-1">Spread <SortIcon column="spread" /></div>
                    </th>
                    <th>Margin Requirement (Leverage)</th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('commission')}>
                      <div className="flex items-center gap-1">Commission <SortIcon column="commission" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('swapShort')}>
                      <div className="flex items-center gap-1">Swap <SortIcon column="swapShort" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort('tradingAmount')}>
                      <div className="flex items-center gap-1">Trading Amount <SortIcon column="tradingAmount" /></div>
                    </th>
                    <th>
                      <div className="flex items-center gap-1">
                        <Activity size={12} className="text-[#00BCD4]" />
                        Virtual Price
                      </div>
                    </th>
                    <th>Edited</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstruments.map((inst, idx) => (
                    <tr 
                      key={idx}
                      className={`cursor-pointer hover:bg-blue-50 ${selectedInstruments.includes(inst.symbol) ? "bg-[#00BCD4]/10" : ""}`}
                      onClick={() => toggleSelectInstrument(inst.symbol)}
                      onDoubleClick={() => {
                        setSelectedInstruments([inst.symbol]);
                        setShowEditPolicy(true);
                      }}
                      data-testid={`instrument-row-${inst.symbol}`}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={selectedInstruments.includes(inst.symbol)}
                          onChange={(e) => toggleSelectInstrument(inst.symbol, e)}
                        />
                      </td>
                      <td className="text-[#00BCD4] font-semibold">{inst.symbol}</td>
                      <td>{inst.group}</td>
                      <td className="num">{inst.units}</td>
                      <td className="num">{inst.contractSize}</td>
                      <td>{inst.executionMode}</td>
                      <td>{inst.tradability}</td>
                      <td>{inst.spread}</td>
                      <td><LeverageBadge value={inst.leverage} /></td>
                      <td className="num">{inst.commission}</td>
                      <td className="text-xs">Short: {inst.swapShort}, Long: {inst.swapLong}</td>
                      <td className="text-xs">{inst.tradingAmount}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                            inst.virtualPriceActive 
                              ? 'bg-green-100 text-green-700 animate-pulse' 
                              : 'bg-gray-100 hover:bg-[#00BCD4]/20 text-gray-600 hover:text-[#00BCD4]'
                          }`}
                          onClick={() => handleOpenVirtualPriceController(inst)}
                          data-testid={`virtual-price-btn-${inst.symbol}`}
                        >
                          <DollarSign size={12} />
                          {inst.virtualPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: inst.decimals || 2 }) || '-'}
                          {inst.virtualPriceActive && <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>}
                        </button>
                      </td>
                      <td>
                        <input type="checkbox" checked={inst.edited} readOnly />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
            <div className="p-2 border-t text-xs text-gray-500 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input type="checkbox" defaultChecked />
                <span>[Tradability] = 'Full Trade' ▼</span>
              </div>
              <div>
                Selected: {selectedInstruments.length} | Total: {filteredInstruments.length}
              </div>
            </div>
          </div>
          </>}
        </div>

        <div className="sirix-modal-footer">
          <button className="sirix-btn sirix-btn-primary" data-testid="term-done" onClick={onClose}>
            ✓ Tamam
          </button>
          <button className="sirix-btn sirix-btn-danger" data-testid="term-cancel" onClick={onClose}>
            ✗ Vazgeç
          </button>
        </div>
      </div>

      {/* Edit Instruments Policy Modal */}
      {showEditPolicy && selectedInstruments.length > 0 && (
        <EditInstrumentsPolicyModal
          instruments={filteredInstruments.filter(i => selectedInstruments.includes(i.symbol))}
          onClose={() => setShowEditPolicy(false)}
          onSave={handleEditSave}
        />
      )}

      {/* Add Instrument Modal */}
      {showAddInstrument && (
        <AddInstrumentModal
          onClose={() => setShowAddInstrument(false)}
          onAdd={handleAddInstruments}
          existingInstruments={localInstruments}
        />
      )}

      {/* Virtual Price Controller Modal */}
      {showVirtualPriceController && selectedInstrumentForPrice && (
        <VirtualPriceController
          instrument={selectedInstrumentForPrice}
          onClose={() => {
            setShowVirtualPriceController(false);
            setSelectedInstrumentForPrice(null);
          }}
          onApply={handleApplyVirtualPrice}
        />
      )}
    </div>
  );
};

const InstrumentsPage = () => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [newInstrument, setNewInstrument] = useState({
    symbol: "",
    name: "",
    type: "forex",
    category: "",
    exchange: "",
    pip_value: 0.0001,
    min_lot_size: 0.01,
    max_lot_size: 100,
    leverage: 100,
    spread: 0,
    commission: 0,
    swap_long: 0,
    swap_short: 0,
    tradingview_symbol: ""
  });

  useEffect(() => {
    fetchInstruments();
  }, []);

  const fetchInstruments = async () => {
    try {
      const data = await instrumentsAPI.getAll();
      setInstruments(data);
    } catch (error) {
      toast.error("Failed to fetch instruments");
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstrument = async () => {
    try {
      await instrumentsAPI.create(newInstrument);
      toast.success("Instrument added successfully");
      setShowAddModal(false);
      setNewInstrument({
        symbol: "", name: "", type: "forex", category: "", exchange: "",
        pip_value: 0.0001, min_lot_size: 0.01, max_lot_size: 100, leverage: 100,
        spread: 0, commission: 0, swap_long: 0, swap_short: 0, tradingview_symbol: ""
      });
      fetchInstruments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add instrument");
    }
  };

  const handleUpdateInstrument = async () => {
    try {
      await instrumentsAPI.update(editingInstrument.id, editingInstrument);
      toast.success("Instrument updated successfully");
      setEditingInstrument(null);
      fetchInstruments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update instrument");
    }
  };

  const handleDeleteInstrument = async (instrumentId) => {
    if (!window.confirm("Are you sure you want to delete this instrument?")) return;
    try {
      await instrumentsAPI.delete(instrumentId);
      toast.success("Instrument deleted");
      fetchInstruments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete instrument");
    }
  };

  const handleExportCSV = () => {
    const headers = ["Symbol", "Name", "Type", "Category", "Exchange", "Leverage", "Min Lot", "Max Lot", "Spread", "Commission", "Swap Long", "Swap Short", "TradingView Symbol", "Status"];
    const rows = filteredInstruments.map(i => [
      i.symbol, i.name, i.type, i.category, i.exchange || "", 
      `1:${i.leverage}`, i.min_lot_size, i.max_lot_size, i.spread, 
      `${i.commission}%`, i.swap_long, i.swap_short, i.tradingview_symbol || "", 
      i.is_active ? "Active" : "Inactive"
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `instruments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  const filteredInstruments = instruments.filter(i =>
    i.symbol?.toLowerCase().includes(search.toLowerCase()) ||
    i.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-4">
          <h1 className="sirix-page-title" style={{ padding: 0, margin: 0 }}>INSTRUMENTS</h1>
          <div className="sirix-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="instruments-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            className="sirix-btn sirix-btn-primary"
            onClick={() => setShowAddModal(true)}
            data-testid="add-instrument-btn"
          >
            <Plus size={14} />
            Add Instrument
          </button>
          <button 
            className="sirix-btn sirix-btn-secondary"
            onClick={handleExportCSV}
            data-testid="export-instruments-btn"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto bg-white">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="sirix-spinner"></div>
          </div>
        ) : (
          <table className="sirix-table" data-testid="instruments-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Exchange</th>
                <th>Leverage</th>
                <th>Min Lot</th>
                <th>Max Lot</th>
                <th>Spread</th>
                <th>Commission</th>
                <th>Swap Long</th>
                <th>Swap Short</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInstruments.map((instrument) => (
                <tr key={instrument.id}>
                  <td className="text-[#00BCD4] font-semibold">{instrument.symbol}</td>
                  <td>{instrument.name}</td>
                  <td>{instrument.type}</td>
                  <td>{instrument.category}</td>
                  <td>{instrument.exchange || "-"}</td>
                  <td className="num">1:{instrument.leverage}</td>
                  <td className="num">{instrument.min_lot_size}</td>
                  <td className="num">{instrument.max_lot_size}</td>
                  <td className="num">{instrument.spread}</td>
                  <td className="num">{instrument.commission}%</td>
                  <td className="num">{instrument.swap_long}</td>
                  <td className="num">{instrument.swap_short}</td>
                  <td>
                    <span className={instrument.is_active ? "sirix-badge sirix-badge-active" : "sirix-badge sirix-badge-inactive"}>
                      {instrument.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1 hover:bg-gray-100 rounded text-blue-600"
                        onClick={() => setEditingInstrument({...instrument})}
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                        onClick={() => handleDeleteInstrument(instrument.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="sirix-table-footer">
        <div>Count={filteredInstruments.length}</div>
        <div></div>
      </div>

      {/* Add Instrument Modal */}
      {showAddModal && (
        <div className="sirix-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="sirix-modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
            <div className="sirix-modal-header">
              <h2 className="sirix-modal-title">Add New Instrument</h2>
              <button className="sirix-modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="sirix-modal-body p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Symbol *</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={newInstrument.symbol}
                    onChange={(e) => setNewInstrument({ ...newInstrument, symbol: e.target.value })}
                    placeholder="e.g., EURUSD"
                    data-testid="new-instrument-symbol"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Name *</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={newInstrument.name}
                    onChange={(e) => setNewInstrument({ ...newInstrument, name: e.target.value })}
                    placeholder="e.g., Euro / US Dollar"
                    data-testid="new-instrument-name"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Type *</label>
                  <select
                    className="sirix-form-select"
                    value={newInstrument.type}
                    onChange={(e) => setNewInstrument({ ...newInstrument, type: e.target.value })}
                  >
                    <option value="forex">Forex</option>
                    <option value="stock">Stock</option>
                    <option value="crypto">Crypto</option>
                    <option value="commodity">Commodity</option>
                    <option value="index">Index</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Category *</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={newInstrument.category}
                    onChange={(e) => setNewInstrument({ ...newInstrument, category: e.target.value })}
                    placeholder="e.g., Major, BIST, Crypto"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Exchange</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={newInstrument.exchange}
                    onChange={(e) => setNewInstrument({ ...newInstrument, exchange: e.target.value })}
                    placeholder="e.g., BIST, NASDAQ"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Leverage</label>
                  <select
                    className="sirix-form-select"
                    value={newInstrument.leverage}
                    onChange={(e) => setNewInstrument({ ...newInstrument, leverage: parseInt(e.target.value) })}
                  >
                    <option value={10}>1:10</option>
                    <option value={20}>1:20</option>
                    <option value={50}>1:50</option>
                    <option value={100}>1:100</option>
                    <option value={200}>1:200</option>
                    <option value={500}>1:500</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Min Lot Size</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.min_lot_size}
                    onChange={(e) => setNewInstrument({ ...newInstrument, min_lot_size: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Max Lot Size</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.max_lot_size}
                    onChange={(e) => setNewInstrument({ ...newInstrument, max_lot_size: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Spread</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.spread}
                    onChange={(e) => setNewInstrument({ ...newInstrument, spread: parseFloat(e.target.value) })}
                    step="0.1"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Commission (%)</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.commission}
                    onChange={(e) => setNewInstrument({ ...newInstrument, commission: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Swap Long</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.swap_long}
                    onChange={(e) => setNewInstrument({ ...newInstrument, swap_long: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Swap Short</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={newInstrument.swap_short}
                    onChange={(e) => setNewInstrument({ ...newInstrument, swap_short: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group col-span-2">
                  <label className="sirix-form-label">TradingView Symbol</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={newInstrument.tradingview_symbol}
                    onChange={(e) => setNewInstrument({ ...newInstrument, tradingview_symbol: e.target.value })}
                    placeholder="e.g., FX:EURUSD, BIST:THYAO"
                  />
                </div>
              </div>
            </div>
            <div className="sirix-modal-footer">
              <button className="sirix-btn sirix-btn-primary" onClick={handleAddInstrument} data-testid="submit-add-instrument">
                ✓ OK
              </button>
              <button className="sirix-btn sirix-btn-danger" onClick={() => setShowAddModal(false)}>
                ✗ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Instrument Modal */}
      {editingInstrument && (
        <div className="sirix-modal-overlay" onClick={() => setEditingInstrument(null)}>
          <div className="sirix-modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
            <div className="sirix-modal-header">
              <h2 className="sirix-modal-title">Edit Instrument: {editingInstrument.symbol}</h2>
              <button className="sirix-modal-close" onClick={() => setEditingInstrument(null)}>×</button>
            </div>
            <div className="sirix-modal-body p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Symbol</label>
                  <input type="text" className="sirix-form-input bg-gray-100" value={editingInstrument.symbol} disabled />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Name</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={editingInstrument.name || ""}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, name: e.target.value })}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Type</label>
                  <select
                    className="sirix-form-select"
                    value={editingInstrument.type || "forex"}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, type: e.target.value })}
                  >
                    <option value="forex">Forex</option>
                    <option value="crypto">Cryptocurrency</option>
                    <option value="stock">Stock</option>
                    <option value="commodity">Commodity</option>
                    <option value="index">Index</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Category</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={editingInstrument.category || ""}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, category: e.target.value })}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Exchange</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={editingInstrument.exchange || ""}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, exchange: e.target.value })}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Leverage</label>
                  <select
                    className="sirix-form-select"
                    value={editingInstrument.leverage}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, leverage: parseInt(e.target.value) })}
                  >
                    <option value={5}>1:5</option>
                    <option value={10}>1:10</option>
                    <option value={20}>1:20</option>
                    <option value={50}>1:50</option>
                    <option value={100}>1:100</option>
                    <option value={200}>1:200</option>
                    <option value={500}>1:500</option>
                  </select>
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Min Lot Size</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.min_lot_size}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, min_lot_size: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Max Lot Size</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.max_lot_size}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, max_lot_size: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Spread</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.spread}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, spread: parseFloat(e.target.value) })}
                    step="0.1"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Commission (%)</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.commission}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, commission: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Swap Long</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.swap_long}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, swap_long: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Swap Short</label>
                  <input
                    type="number"
                    className="sirix-form-input"
                    value={editingInstrument.swap_short}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, swap_short: parseFloat(e.target.value) })}
                    step="0.01"
                  />
                </div>
                <div className="sirix-form-group">
                  <label className="sirix-form-label">Status</label>
                  <select
                    className="sirix-form-select"
                    value={editingInstrument.is_active ? "active" : "inactive"}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, is_active: e.target.value === "active" })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="sirix-form-group col-span-2">
                  <label className="sirix-form-label">TradingView Symbol</label>
                  <input
                    type="text"
                    className="sirix-form-input"
                    value={editingInstrument.tradingview_symbol || ""}
                    onChange={(e) => setEditingInstrument({ ...editingInstrument, tradingview_symbol: e.target.value })}
                    placeholder="e.g., FX:EURUSD, BIST:THYAO"
                  />
                </div>
              </div>
            </div>
            <div className="sirix-modal-footer">
              <button className="sirix-btn sirix-btn-primary" onClick={handleUpdateInstrument}>
                ✓ Save Changes
              </button>
              <button className="sirix-btn sirix-btn-danger" onClick={() => setEditingInstrument(null)}>
                ✗ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { TradingTermsModal, EditInstrumentsPolicyModal };
export default TradingTerms;
